import { useEffect, useState } from "react";
import { Activity, CheckCircle2, AlertTriangle, Play, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function CampaignDiagnose({ campaignId, onAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/campaigns/${campaignId}/diagnose`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnose failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [campaignId]);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post(`/campaigns/${campaignId}/process-now`);
      toast.success(`Send tick triggered - ${r.data.attempted} attempt${r.data.attempted === 1 ? "" : "s"}`);
      load();
      onAction?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to trigger");
    } finally {
      setSending(false);
    }
  };

  const requeue = async () => {
    setSending(true);
    try {
      const r = await api.post(`/campaigns/${campaignId}/resume-leads`);
      toast.success(`Re-queued ${r.data.requeued} lead${r.data.requeued === 1 ? "" : "s"}`);
      load();
      onAction?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-queue failed");
    } finally {
      setSending(false);
    }
  };

  if (!data && loading)
    return <div className="text-neutral-500 text-sm font-mono">Diagnosing…</div>;
  if (!data) return null;

  const healthy = data.healthy;
  return (
    <div
      className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5 space-y-4"
      data-testid="campaign-diagnose"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Send diagnostics
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-400 hover:text-white border border-[#27272a] rounded-md px-2.5 py-1.5"
            data-testid="diagnose-refresh"
          >
            <RefreshCw
              className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}
              strokeWidth={1.5}
            />
            Refresh
          </button>
          <button
            onClick={requeue}
            disabled={sending}
            className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-amber-400/90 hover:text-amber-300 border border-amber-400/30 rounded-md px-2.5 py-1.5 disabled:opacity-50"
            data-testid="diagnose-requeue"
          >
            Re-queue stuck leads
          </button>
          <button
            onClick={sendNow}
            disabled={sending}
            className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.18em] bg-white text-black rounded-md px-3 py-1.5 hover:bg-neutral-200 disabled:opacity-50"
            data-testid="diagnose-send-now"
          >
            <Play className="w-3 h-3" strokeWidth={1.8} />
            Send now
          </button>
        </div>
      </div>

      <div
        className={`flex items-start gap-2 rounded-md border p-3 ${
          healthy
            ? "border-emerald-400/20 bg-emerald-400/5 text-emerald-300"
            : "border-amber-400/20 bg-amber-400/5 text-amber-200"
        }`}
        data-testid={healthy ? "diagnose-healthy" : "diagnose-blocked"}
      >
        {healthy ? (
          <CheckCircle2 className="w-4 h-4 mt-0.5" strokeWidth={1.8} />
        ) : (
          <AlertTriangle className="w-4 h-4 mt-0.5" strokeWidth={1.8} />
        )}
        <div className="flex-1">
          {healthy ? (
            <span className="text-sm">
              All systems go. Next tick will pick up any due leads.
            </span>
          ) : (
            <div className="space-y-1">
              {data.blockers.map((b, i) => (
                <div key={`${i}-${b.slice(0, 32)}`} className="text-sm leading-snug">
                  • {b}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono">
        <Tile label="Within window" value={data.within_window_now ? "yes" : "no"} good={data.within_window_now} />
        <Tile label="Leads due now" value={data.leads_due_now} good={data.leads_due_now > 0} />
        <Tile label="Daily cap used" value={`${data.sent_today}/${data.daily_cap}`} good={data.cap_remaining > 0} />
        <Tile label="Failed today" value={data.failed_sends_today} good={data.failed_sends_today === 0} />
      </div>

      <div className="text-xs font-mono text-neutral-500 space-y-1.5">
        <div>
          <span className="text-neutral-600">Timezone:</span> {data.timezone} ·{" "}
          <span className="text-neutral-600">Window:</span>{" "}
          {data.sending_window?.start_hour ?? 0}:00–{data.sending_window?.end_hour ?? 24}:00 ·{" "}
          <span className="text-neutral-600">Days:</span> {(data.sending_window?.days || []).length}/7
        </div>
        {data.next_window_open_utc && (
          <div>
            <span className="text-neutral-600">Next window opens:</span>{" "}
            {new Date(data.next_window_open_utc).toLocaleString()}
          </div>
        )}
        <div>
          <span className="text-neutral-600">Leads by status:</span>{" "}
          {Object.entries(data.leads_by_status)
            .map(([k, v]) => `${k}:${v}`)
            .join(" · ") || "none"}
        </div>
      </div>

      {data.inboxes.length > 0 && (
        <div className="border-t border-[#1a1a1a] pt-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono mb-2">
            Inboxes
          </div>
          <div className="space-y-1.5">
            {data.inboxes.map((i) => (
              <div
                key={i.id}
                className="flex items-center justify-between gap-2 text-xs font-mono"
                data-testid={`diagnose-inbox-${i.id}`}
              >
                <span className="text-neutral-300">{i.email}</span>
                <div className="flex items-center gap-3 text-neutral-500">
                  <span>{i.connection_type}</span>
                  <span>
                    {i.sent_today}/{i.daily_limit}
                  </span>
                  <span
                    className={
                      i.status === "active"
                        ? "text-emerald-400"
                        : i.status === "error"
                        ? "text-red-400"
                        : "text-amber-400"
                    }
                  >
                    {i.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {data.inboxes.some((i) => i.last_error) && (
            <div className="mt-2 text-xs text-red-400/80 font-mono">
              last error: {data.inboxes.find((i) => i.last_error)?.last_error?.slice(0, 200)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, good }) {
  return (
    <div
      className={`border rounded-md p-2.5 ${
        good ? "border-[#1a1a1a] bg-[#0a0a0a]" : "border-amber-400/30 bg-amber-400/5"
      }`}
    >
      <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
      <div className="mt-0.5 text-sm">{value}</div>
    </div>
  );
}
